import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react-native';
import { useCallback, useState } from 'react';
import { Platform } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { ImagePreviewSheet } from './ImagePreviewSheet';
import type { ThreadFile } from './threadItems';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Image } from '@/components/ui/image';
import { Pressable } from '@/components/ui/pressable';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { FIVE_MINUTES } from '@/constants/time';
import { useToaster } from '@/hooks/useToaster';
import { getSupportFileUrl } from '@/services/conversations/attachments';
import { downloadFile } from '@/util/downloadFile';
import { Sentry } from '@/util/sentry';

/**
 * Image types every platform can draw inline. HEIC draws on iOS only, so it
 * shows as a file everywhere rather than as a broken picture on web and Android.
 */
const PREVIEWABLE_IMAGE_TYPES: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

/** Download URLs are short-lived; refetch well before one expires. */
const FILE_URL_STALE_TIME = FIVE_MINUTES;

export function isPreviewableImage(mimeType: string): boolean {
  return PREVIEWABLE_IMAGE_TYPES.has(mimeType);
}

const fileUrlQueryKey = (s3Key: string) => ['supportFileUrl', s3Key] as const;

type SupportMessageAttachmentsProps = {
  files: ThreadFile[];
};

/**
 * A message's files under its text: pictures as thumbnails, everything else
 * as a named chip. Pressing a thumbnail opens an in-app preview with a
 * download action; pressing a chip opens the file through the platform's
 * viewer or share sheet. A file still uploading previews from this device but
 * cannot be downloaded until the send lands, since it has no stored copy yet.
 *
 * The chips sit on the card surface in both bubbles, so their text keeps the
 * same contrast on the reader's brand-filled messages as on the other side's.
 */
export function SupportMessageAttachments({ files }: SupportMessageAttachmentsProps) {
  if (files.length === 0) {
    return null;
  }

  const images = files.filter((file) => isPreviewableImage(file.mimeType));

  const documents = files.filter((file) => !isPreviewableImage(file.mimeType));

  return (
    <VStack className="mt-2 gap-2">
      {images.length > 0 ? (
        <HStack className="flex-wrap gap-2">
          {images.map((file) => (
            <ImageThumbnail key={file.key} file={file} />
          ))}
        </HStack>
      ) : null}

      {documents.map((file) => (
        <FileChip key={file.key} file={file} />
      ))}
    </VStack>
  );
}

/** Opens a stored file, fetching a fresh URL each time since a cached one may have expired. */
function useOpenFile(file: ThreadFile) {
  const toast = useToaster();

  const [isOpening, setIsOpening] = useState(false);

  const open = useCallback(async () => {
    if (!file.s3Key || isOpening) {
      return;
    }

    setIsOpening(true);

    try {
      const url = await getSupportFileUrl(file.s3Key, 'download');

      await downloadFile({ url, fileName: file.name });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'SupportMessageAttachments', source: 'support-thread' },
        extra: {
          errorMessage: error instanceof Error ? error.message : undefined,
          mimeType: file.mimeType,
        },
      });

      toast.showError({ title: 'Couldn’t open the file', message: 'Please try again.' });
    } finally {
      setIsOpening(false);
    }
  }, [file.s3Key, file.name, file.mimeType, isOpening, toast]);

  return { open, isOpening, canOpen: Boolean(file.s3Key) };
}

function ImageThumbnail({ file }: { file: ThreadFile }) {
  const { open, canOpen } = useOpenFile(file);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  /*
   * The sheet is built on first press and kept after that so it can animate
   * out. A thread can hold many pictures, and at most one preview is ever open,
   * so mounting a sheet and its hooks per thumbnail up front is wasted work.
   */
  const [hasOpenedPreview, setHasOpenedPreview] = useState(false);

  const storedUrl = useQuery({
    queryKey: fileUrlQueryKey(file.s3Key ?? ''),
    queryFn: () => getSupportFileUrl(file.s3Key ?? '', 'render'),
    enabled: Boolean(file.s3Key) && !file.uri,
    staleTime: FILE_URL_STALE_TIME,
  });

  const uri = file.uri ?? storedUrl.data;

  /**
   * Opening the preview is the view. The picture has already drawn as a
   * thumbnail, which the server does not report, so this asks again with the
   * reason and ignores the answer. Only a stored file has anything to report:
   * a picture still uploading previews from this device.
   */
  const reportView = () => {
    if (!file.s3Key) {
      return;
    }

    getSupportFileUrl(file.s3Key, 'view').catch(() => undefined);
  };

  const downloadFromPreview = async () => {
    setIsPreviewOpen(false);

    await open();
  };

  return (
    <>
      <Pressable
        onPress={() => {
          setHasOpenedPreview(true);

          setIsPreviewOpen(true);

          reportView();
        }}
        disabled={!uri}
        accessibilityRole="button"
        accessibilityLabel={`Preview image ${file.name}`}
        className="h-32 w-32 overflow-hidden rounded-xl border border-outline-200 bg-background-50 web:cursor-pointer">
        {uri ? (
          <Image
            source={{ uri }}
            alt={file.name}
            size="full"
            resizeMode="cover"
            accessibilityIgnoresInvertColors
          />
        ) : (
          <View className="h-full w-full items-center justify-center">
            {storedUrl.isError ? (
              <Icon as={FileText} className="h-6 w-6 text-typography-700" />
            ) : (
              <Spinner size="small" accessibilityLabel="Loading image" />
            )}
          </View>
        )}
      </Pressable>

      {uri && hasOpenedPreview ? (
        <ImagePreviewSheet
          isOpen={isPreviewOpen}
          uri={uri}
          name={file.name}
          onClose={() => setIsPreviewOpen(false)}
          onDownload={canOpen ? downloadFromPreview : undefined}
        />
      ) : null}
    </>
  );
}

function FileChip({ file }: { file: ThreadFile }) {
  const { open, isOpening, canOpen } = useOpenFile(file);

  return (
    <Pressable
      onPress={open}
      disabled={!canOpen}
      accessibilityRole="button"
      accessibilityLabel={`Open file ${file.name}`}
      className={twJoin(
        'min-h-12 flex-row items-center gap-2 rounded-xl border border-outline-200 bg-background-0 px-3 py-2',
        Platform.OS === 'web' && canOpen && 'cursor-pointer data-[hover=true]:bg-background-50'
      )}>
      {isOpening ? (
        <Spinner size="small" accessibilityLabel="Opening file" />
      ) : (
        <Icon as={FileText} className="h-5 w-5 text-typography-700" />
      )}

      <Text className="min-w-0 shrink text-sm text-typography-900" numberOfLines={1}>
        {file.name}
      </Text>
    </Pressable>
  );
}
